import React, { useEffect, useState, useContext, useRef } from 'react';
import {
  Card,
  Col,
  Form,
  OverlayTrigger,
  Row,
  Tooltip,
  Button,
  InputGroup,
  Alert
} from 'react-bootstrap';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import ProductList from './ProductList';
import ProductGrid from './ProductGrid';
import CartModal from '../cart/CartModal';
import usePagination from 'y4/hooks/usePagination';
import Flex from 'components/common/Flex';
import useProduct from 'y4/hooks/useProduct';
import { useTranslation, Trans } from 'react-i18next';
import AppContext, { ProductContext } from 'context/Context';
import { initWinXinShare } from 'y4/WeiXin';
import y from 'y4/Utils';
import SearchBox from 'y4/components/navbar/top/SearchBox';
import Lottie from 'lottie-react';
import infiniteLoop from 'assets/img/animated-icons/infinite-loop.json';
import _ from 'lodash';

const Products = () => {
  const {
    config: { isNavbarVerticalCollapsed },
    setConfig
  } = useContext(AppContext);

  const productsNavbarVerticalCollapsed = useRef(isNavbarVerticalCollapsed);
  const location = useLocation();
  const {
    productsState: { config }
  } = useContext(ProductContext);

  const [sortBy, setSortBy] = useState(
    y.isEmpty(config.orderFields[0]) ? null : config.orderFields[0].name
  );
  const [isAsc, setIsAsc] = useState(true);
  const [keyword, setKeyword] = useState(y.getQuery(location.search, 'kw'));
  const { productLayout, corp } = useParams();
  const layout = productLayout.split(/-/)[1];
  const isList = layout === 'list';
  const isGrid = layout === 'grid';
  const navigate = useNavigate();

  useEffect(() => {
    isList || isGrid || navigate(`${corp}/errors/404`);
    y.setNavbarTopSearchBox({
      placeholder: config.placeholder,
      search,
      value: keyword
    });

    const wx_config = _.get(config, 'option.weixin');
    if (wx_config) {
      initWinXinShare({
        config: wx_config,
        title: _.get(wx_config, 'share.title'),
        link: window.location.href,
        description: _.get(wx_config, 'share.description'),
        imageUrl: _.get(wx_config, 'share.image')
      });
    }
  }, []);

  useEffect(() => {
    setConfig('isNavbarVerticalCollapsed', true);

    return () => {
      setConfig(
        'isNavbarVerticalCollapsed',
        productsNavbarVerticalCollapsed.current
      );
    };
  }, []);

  const {
    paginationState: {
      data: paginatedProducts,
      totalItems,
      currentPage,
      canNextPage,
      canPreviousPage,
      paginationArray,
      itemsLoaded,
      from,
      to
    },
    nextPage,
    prevPage,
    goToPage,
    initItems
  } = usePagination(useProduct(config.pageBy != 'none', 12), 12);

  const search = input => {
    if (!input) input = '';
    setKeyword(input.trim());
    initItems({
      keyword: input.trim(),
      sortBy,
      order: isAsc ? 'asc' : 'desc'
    });
    navigate(`/${corp}/e-commerce/product/product-grid?kw=${input || ''}`);
  };

  //Show page 1 after shown
  useEffect(() => {
    initItems({
      keyword,
      sortBy,
      order: isAsc ? 'asc' : 'desc'
    });
  }, [sortBy, isAsc]);

  const { t } = useTranslation('commerce');
  const m1 = t(`Showing {from}-{to} of {totalItems} Products`);
  const m2 = y.formatByNamedArgs(m1, {
    from: totalItems == 0 ? 0 : from,
    to,
    totalItems
  });

  const getPageArray = count => {
    if (paginationArray.length <= count) return paginationArray;
    const p1 = Math.floor(count / 2);
    const p2 = [];
    if (currentPage > paginationArray.length - p1) {
      for (let i = 0; i < count; i++) {
        p2.push(paginationArray.length - count + i + 1);
      }
    } else if (currentPage > p1) {
      for (let i = 0; i < count; i++) {
        p2.push(currentPage - p1 + i);
      }
    } else {
      for (let i = 0; i < count; i++) {
        p2.push(1 + i);
      }
    }
    return p2;
  };

  return (
    <>
      <Card className="mb-3">
        <Card.Body>
          <Row className="flex-between-center">
            <Col
              sm="auto"
              as={Flex}
              alignItems="center"
              className="mb-2 mb-sm-0 d-lg-none"
            >
              <SearchBox
                placeholder={config.placeholder}
                search={search}
                size="sm"
              ></SearchBox>
            </Col>
            <Col
              sm="auto"
              as={Flex}
              alignItems="start"
              className="mb-2 mb-sm-0 d-none d-lg-block"
            >
              <h6 className="mb-0 ms-2">{m2}</h6>
            </Col>
            {y.isEmpty(config.orderFields) ? null : (
              <Col sm="auto" className="d-none d-lg-block">
                <Row className="gx-2 align-items-center">
                  <Col xs="auto">
                    <Form as={Row} className="gx-2">
                      <Col xs="auto">
                        <small>
                          <Trans ns="commerce">Sort by:</Trans>
                        </small>
                      </Col>
                      <Col xs="auto">
                        <InputGroup size="sm">
                          <Form.Select
                            className="pe-5"
                            defaultValue={config.orderFields[0].name}
                            onChange={({ target }) => setSortBy(target.value)}
                          >
                            {config.orderFields.map((item, index) => {
                              return (
                                <option key={index} value={item.name}>
                                  {item.value}
                                </option>
                              );
                            })}
                          </Form.Select>
                          <InputGroup.Text
                            as={Button}
                            variant="link"
                            className="border border-300 text-700"
                            onClick={() => setIsAsc(!isAsc)}
                          >
                            <FontAwesomeIcon
                              icon={
                                isAsc ? 'sort-amount-up' : 'sort-amount-down'
                              }
                            />
                          </InputGroup.Text>
                        </InputGroup>
                      </Col>
                    </Form>
                  </Col>
                  <Col xs="auto" className="pe-0">
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip>Product {isList ? 'Grid' : 'List'}</Tooltip>
                      }
                    >
                      <Link
                        to={`/${corp}/e-commerce/product/product-${
                          isList ? 'grid' : 'list'
                        }`}
                        className="text-600 px-1"
                      >
                        <FontAwesomeIcon
                          icon={classNames({ th: isList, 'list-ul': isGrid })}
                        />
                      </Link>
                    </OverlayTrigger>
                  </Col>
                </Row>
              </Col>
            )}
          </Row>
        </Card.Body>
      </Card>
      <Card className="mb-3">
        <Card.Body
          className={classNames({
            'p-0 overflow-hidden': isList,
            'pb-0': isGrid
          })}
        >
          <Row
            className={classNames({
              'g-0': isList
            })}
          >
            {!itemsLoaded ? (
              <Lottie
                animationData={infiniteLoop}
                loop={true}
                style={{ height: '120px', width: '120px' }}
              />
            ) : totalItems == 0 ? (
              <div className="px-5 py-4">
                <Alert variant="warning">
                  <Alert.Heading>
                    <Trans ns="commerce">No Goods</Trans>
                  </Alert.Heading>
                  <p className="mb-0">
                    <Trans ns="commerce">
                      Nothing found,change the keyword and try again
                    </Trans>
                  </p>
                </Alert>
              </div>
            ) : (
              paginatedProducts.map((product, index) =>
                layout === 'list' ? (
                  <ProductList product={product} key={index} index={index} />
                ) : (
                  <ProductGrid
                    product={product}
                    key={index}
                    lg={6}
                    xl={4}
                    xxl={3}
                    index={index}
                  />
                )
              )
            )}
          </Row>
        </Card.Body>
        <Card.Footer
          className={classNames('d-flex justify-content-center', {
            'bg-light mt-n1': isGrid,
            'border-top': isList
          })}
        >
          <div>
            <OverlayTrigger placement="top" overlay={<Tooltip>Prev</Tooltip>}>
              <Button
                variant="falcon-default"
                size="sm"
                disabled={!canPreviousPage}
                onClick={prevPage}
                className="me-2"
                trigger="focus"
              >
                <FontAwesomeIcon icon="chevron-left" />
              </Button>
            </OverlayTrigger>
          </div>

          <ul className="pagination mb-0">
            {getPageArray(5).map(page => (
              <li
                key={page}
                className={classNames({ active: currentPage === page })}
              >
                <Button
                  size="sm"
                  variant="falcon-default"
                  className="page me-2"
                  onClick={() => goToPage(page)}
                >
                  {page}
                </Button>
              </li>
            ))}
          </ul>
          <div>
            <OverlayTrigger
              trigger="click"
              placement="top"
              overlay={<Tooltip id="button-tooltip-2">Next</Tooltip>}
            >
              <Button
                variant="falcon-default"
                size="sm"
                disabled={!canNextPage}
                onClick={nextPage}
                trigger="focus"
              >
                <FontAwesomeIcon icon="chevron-right" />
              </Button>
            </OverlayTrigger>
          </div>
        </Card.Footer>
      </Card>
      <CartModal />
    </>
  );
};

export default Products;
